import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import './HeroSection.css';
import { useNavigate } from 'react-router-dom';

// Import your logo file (make sure the path is correct)
import companyLogo from '../Images/SmartlightLogoWhite.png';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

export default function HeroSection() {
  const bgRef = useRef(null);
  const whiteSvgRef = useRef(null);
  const sphere1Ref = useRef(null);
  const sphere2Ref = useRef(null);
  const contentRef = useRef(null);
  const logoRef = useRef(null);
  const buttonRef = useRef(null);

  // Refs for the terminal typewriter effect
  const subtitleContainerRef = useRef(null);
  const itemRef = useRef(null);
  const cloneRef = useRef(null);

  const navigate = useNavigate();

  const handleNavigateToContact = () => {
    navigate('/contactUs'); // This path should match your route config
  };

  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    const isSmallMobile = window.innerWidth <= 480;

    const bgSpeed = isMobile ? 0.0005 : 0.001;
    const whiteSvgSpeed = isMobile ? 0.0008 : 0.0015;
    const sphere1Speed = isMobile ? 0.001 : 0.002;
    const sphere2Speed = isMobile ? 0.0008 : 0.0015;

    // Responsive GSAP ScrollTrigger parallax effect
    gsap.timeline({
      scrollTrigger: {
        trigger: '.hero-section',
        start: 'top top',
        end: 'bottom top',
        scrub: 3,
        onUpdate: (self) => {
          const scrollY = self.scroll();
          if (bgRef.current) { gsap.set(bgRef.current, { y: scrollY * bgSpeed }); }
          if (whiteSvgRef.current) { gsap.set(whiteSvgRef.current, { y: scrollY * whiteSvgSpeed }); }
          if (sphere1Ref.current) { gsap.set(sphere1Ref.current, { y: scrollY * sphere1Speed }); }
          if (sphere2Ref.current) { gsap.set(sphere2Ref.current, { y: scrollY * sphere2Speed }); }
          if (contentRef.current && !isSmallMobile) { gsap.set(contentRef.current, { opacity: Math.max(0.8, 1 - (scrollY * 0.0005)) }); }
        }
      }
    });

    const entranceDelay = isMobile ? 0.1 : 0.2;
    const animationDuration = isMobile ? 0.8 : 1;

    gsap.fromTo(bgRef.current, { opacity: 0 }, { opacity: 1, duration: animationDuration, ease: "power2.out" });
    gsap.fromTo(whiteSvgRef.current, { opacity: 0 }, { opacity: 1, duration: animationDuration, delay: entranceDelay * 0.5, ease: "power2.out" });
    gsap.fromTo(sphere1Ref.current, { opacity: 0, y: isMobile ? 20 : 30 }, { opacity: 1, y: 0, duration: animationDuration, delay: entranceDelay, ease: "power2.out" });
    gsap.fromTo(sphere2Ref.current, { opacity: 0, y: isMobile ? -20 : -30 }, { opacity: 1, y: 0, duration: animationDuration, delay: entranceDelay * 2, ease: "power2.out" });
    gsap.fromTo(logoRef.current, { opacity: 0, x: isMobile ? 30 : 50 }, { opacity: 1, x: 0, duration: animationDuration * 0.8, delay: entranceDelay * 3, ease: "power2.out" });
    gsap.fromTo(buttonRef.current, { opacity: 0, x: isMobile ? 30 : 50 }, { opacity: 1, x: 0, duration: animationDuration * 0.8, delay: entranceDelay * 4, ease: "power2.out" });
    gsap.fromTo(subtitleContainerRef.current, { opacity: 0 }, { opacity: 1, duration: animationDuration * 0.6, delay: entranceDelay * 3.5, ease: "power2.out" });

    // Terminal Typewriter Effect Logic
    let typewriterInterval;
    const fullText = "Seek your Inner light!";
    const startTypingDelay = (entranceDelay * 3.5 + (animationDuration * 0.6)) * 1000; // Delay in milliseconds

    const startTypingTimeout = setTimeout(() => {
      if (subtitleContainerRef.current) {
        subtitleContainerRef.current.classList.add('is-initialized');
      }

      let currentIndex = 0;
      typewriterInterval = setInterval(() => {
        if (currentIndex <= fullText.length) {
          const currentText = fullText.substring(0, currentIndex);
          if (cloneRef.current) cloneRef.current.textContent = currentText;

          if (currentIndex === fullText.length) {
            if (itemRef.current) itemRef.current.classList.remove('show-cursor');
          } else {
            if (itemRef.current) itemRef.current.classList.add('show-cursor');
          }
          currentIndex++;
        } else {
          clearInterval(typewriterInterval);
          if (itemRef.current) itemRef.current.classList.remove('show-cursor');
        }
      }, 80); // Adjust typing speed here (80ms)
    }, startTypingDelay);


    const handleResize = () => { ScrollTrigger.refresh(); };
    window.addEventListener('resize', handleResize);

    // Cleanup function
    return () => {
      clearTimeout(startTypingTimeout);
      if (typewriterInterval) clearInterval(typewriterInterval);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <>
      <div className="hero-section">
        <svg
          ref={whiteSvgRef}
          viewBox="0 100 1900 300"
          xmlns="http://www.w3.org/2000/svg"
          className="hero-bg-svg"
          preserveAspectRatio="xMidYMax meet"
        >
          <path
            d="M -0.069 -1.296 L -0.902 533.576 H 1883.262 V -293.906 C 1773.5133 -98.8327 1807.612 232.792 1554.016 291.314 H 286.039 C 120.227 301.068 120.227 184.024 48.7 79.985"
            fill="#ffffff"
          />
        </svg>
        <motion.div
          ref={sphere1Ref}
          className="floating-sphere sphere-1"
          animate={{ y: [0, 32, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          ref={sphere2Ref}
          className="floating-sphere sphere-2"
          animate={{ y: [0, 24, 0] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <div ref={contentRef} className="hero-content hero-content-right">
          <img
            ref={logoRef}
            src={companyLogo}
            alt="SmartLight Company Logo"
            className="hero-logo"
          />
          <p ref={subtitleContainerRef} className="hero-subtitle terminal-typewriter">
            <u>
              <span className="item" ref={itemRef}>
                <span className="original" aria-hidden="true">Seek your Inner light!</span>
                <span className="clone" aria-hidden="true" ref={cloneRef}></span>
              </span>
            </u>
          </p>
          <motion.button
            ref={buttonRef}
            whileHover={{ scale: 1.05 }}
            className="hero-button"
            onClick={handleNavigateToContact}
          >
            Contact Us
          </motion.button>
        </div>
      </div>
    </>
  );
}